import React from 'react';
import PropTypes from 'prop-types';
import { Line, VictoryChart, VictoryAxis, VictoryBar } from 'victory';

import { getDomainForDay, getBeginDate, getEndDate } from '../../Timeline/Timeline.helpers';
import { timelineChartAxisX } from '../../Timeline/TimelineChart.parts';
import { LabelWithTooltip } from './LabelWithTooltip';
import { TimelineLabel } from '../../Timeline/TimelineLabel';
import { PersonsOffshoreChartRow } from './PersonsOffshoreChartRow/PersonsOffshoreChartRow';
import { ErrorBoundary } from '../../ErrorBoundary/ErrorBoundary';
import { chartTheme, chartLabelStyles, getGridStroke } from '../../../common/ChartTheme';
import { RoundedBottomBorder, RoundedBottomBorderNoData } from '../Chart.styles';
import { barCornerRadius } from '../Chart.utils';
import { defaultVictoryContainer } from '../Chart.defaults';

export const PersonsOffshoreChartVictory = ({ timelineData, selectedDate, chartWidth, itemLabelWidth, theme }) => {
    // To keep row height consistenly at (rowHeight), we have to calculate chart height
    const rowHeight = 32;
    const itemHeight = 18;
    const timeAxisHeight = 35;
    const bordersFix = 2;

    const chartPadding = {
        left: itemLabelWidth - bordersFix / 2,
        right: -bordersFix,
        top: timeAxisHeight,
        bottom: timeAxisHeight,
    };
    function uniqueByKey(array) {
        return array.filter((item, i) => array.findIndex((nextItem) => item.key === nextItem.key) === i);
    }

    if (!timelineData) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} />;
    }

    if (timelineData.length === 0) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} message="No offshore movement for selected period" />;
    }

    const timelineRows = uniqueByKey(timelineData).length;

    const colors = {
        VESSEL: theme.color.blue,
        TURBINE: theme.color.green,
    };
    const calculatedChartHeight = rowHeight * timelineRows + chartPadding.top + chartPadding.bottom;

    return (
        <RoundedBottomBorder>
            <ErrorBoundary>
                <VictoryChart
                    theme={chartTheme}
                    domainPadding={{ x: [-rowHeight / 2, rowHeight / 2] }}
                    height={calculatedChartHeight}
                    width={chartWidth - bordersFix}
                    padding={chartPadding}
                    domain={{
                        y: getDomainForDay(selectedDate),
                        x: [0, timelineRows],
                    }}
                    containerComponent={defaultVictoryContainer}>
                    <VictoryAxis
                        key="1"
                        name="entity-axis"
                        orientation="left"
                        tickLabelComponent={
                            <TimelineLabel
                                data={timelineData}
                                width={itemLabelWidth}
                                rowComponent={<PersonsOffshoreChartRow />}
                            />
                        }
                        gridComponent={<Line x1={0} />}
                        fixLabelOverlap
                        style={{
                            tickLabels: chartLabelStyles,
                            grid: {
                                strokeWidth: rowHeight + 1,
                                stroke: getGridStroke(theme),
                            },
                        }}
                    />
                    {timelineChartAxisX({
                        orientation: 'top',
                        timeAxisHeight,
                        chartDay: selectedDate,
                    })}
                    {timelineChartAxisX({
                        orientation: 'bottom',
                        timeAxisHeight,
                        chartDay: selectedDate,
                    })}

                    <VictoryBar
                        horizontal
                        key="1"
                        name="release-chart"
                        cornerRadius={barCornerRadius}
                        alignment="middle"
                        labels={({ datum }) => datum.name}
                        labelComponent={<LabelWithTooltip chartWidth={chartWidth} />}
                        style={{
                            labels: {
                                fill: 'black',
                                fontSize: 11,
                                padding: 5,
                            },
                            data: {
                                width: itemHeight,
                                fill: ({ datum }) => colors[datum.type],
                                stroke: ({ datum }) => colors[datum.type],
                                strokeWidth: 1,
                            },
                        }}
                        x={(d) => d.key}
                        y={getBeginDate}
                        y0={getEndDate}
                        data={timelineData}
                    />
                </VictoryChart>
            </ErrorBoundary>
        </RoundedBottomBorder>
    );
};

PersonsOffshoreChartVictory.displayName = 'PersonsOffshoreChartVictory';

PersonsOffshoreChartVictory.propTypes = {
    timelineData: PropTypes.array,
    selectedDate: PropTypes.object,
    chartWidth: PropTypes.number,
    itemLabelWidth: PropTypes.number,
};

PersonsOffshoreChartVictory.defaultProps = {
    timelineData: [],
    chartWidth: 1890,
    itemLabelWidth: 400,
};
